// ────────────────────────────────────────────────────────────────────────────
// Ferry-crossing classification.
//
// The TMS doesn't store an order's geography — task addresses are ids into
// Directory. So whether an order needs a ferry is decided here, on the FE,
// from the origin/destination address countries (ISO codes resolved from
// Directory), using a *landmass* rule rather than a naive "different country":
// IE↔XI is two countries but one island (land border) → no ferry.
// ────────────────────────────────────────────────────────────────────────────

/** Landmass groupings — a crossing is needed only *between* different landmasses. */
export type Landmass =
  | 'ireland'
  | 'gb'
  | 'continent'
  | 'isle-of-man'
  | 'jersey'
  | 'guernsey'
  | 'iceland'
  | 'malta'
  | 'cyprus';

// Island of Ireland = Republic (IE) + Northern Ireland (XI). Great Britain = GB.
// The other islands are listed so a GB→IM or IT→MT order still needs a boat;
// everything else is Continental Europe — FR→DE is a border, not a crossing.
const LANDMASS: Record<string, Landmass> = {
  IE: 'ireland',
  XI: 'ireland',
  GB: 'gb',
  IM: 'isle-of-man',
  JE: 'jersey',
  GG: 'guernsey',
  IS: 'iceland',
  MT: 'malta',
  CY: 'cyprus',
};

/**
 * The landmass for an address country or a port's customs territory.
 *
 * <p>
 * Pass a PORT's customs territory, not its ISO country: Belfast is `GB` by its
 * official code (GBBEL) but `XI` by territory, and it is the territory that
 * puts it on the island of Ireland. The route catalogue carries both.
 * </p>
 */
export function toLandmass(countryIso: string | null | undefined): Landmass | null {
  const code = countryIso?.trim().toUpperCase();
  if (!code) return null;
  return LANDMASS[code] ?? 'continent';
}

/**
 * A ferry / sea crossing is needed when origin and destination sit on different
 * landmasses. Symmetric — IE→GB and GB→IE both qualify. Same island (IE↔XI),
 * within GB, or within the continent → no crossing. Returns `null` when either
 * country is unknown (can't classify yet).
 */
export function needsFerry(
  originIso: string | null | undefined,
  destIso: string | null | undefined,
): boolean | null {
  const a = toLandmass(originIso);
  const b = toLandmass(destIso);
  if (a === null || b === null) return null;
  return a !== b;
}

/** Corridor label for the queue chip, e.g. "IE→GB". `null` when either end is unknown. */
export function corridorLabel(
  originIso: string | null | undefined,
  destIso: string | null | undefined,
): string | null {
  const o = originIso?.trim().toUpperCase();
  const d = destIso?.trim().toUpperCase();
  if (!o || !d) return null;
  return `${o}→${d}`;
}

/** Where goods are for customs purposes — XI is its own, not GB. */
export type CustomsTerritory = 'EU' | 'XI' | 'GB' | 'other';

const EU_CUSTOMS_UNION = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI',
  'SK',
]);

// The Crown Dependencies are in the UK's customs territory: GB→IM, GB→JE and
// GB→GG are no customs movement, and IM / JE / GG → EU is, exactly as GB.
const UK_CUSTOMS = new Set(['GB', 'IM', 'JE', 'GG']);

export function toCustomsTerritory(countryIso: string | null | undefined): CustomsTerritory | null {
  const code = countryIso?.trim().toUpperCase();
  if (!code) return null;
  if (code === 'XI') return 'XI';
  if (UK_CUSTOMS.has(code)) return 'GB';
  return EU_CUSTOMS_UNION.has(code) ? 'EU' : 'other';
}

/**
 * Whether moving goods from one place to the other is a customs movement.
 * Direction matters — under the Windsor Framework:
 *
 * <ul>
 *   <li>EU ↔ XI — none: Northern Ireland follows the EU goods rules.</li>
 *   <li>GB → XI — yes (often the lighter green lane, but a declaration).</li>
 *   <li>XI → GB — none: "unfettered access".</li>
 *   <li>GB ↔ EU — yes, full customs.</li>
 *   <li>Anything with a non-EU, non-UK country (NO, CH, …) — yes, unless it
 *       is the same country.</li>
 * </ul>
 *
 * <p>
 * Pass the GOODS' from / to — the stops either side of the crossing, whose
 * address countries already say XI — not the ports. Returns `null` when either
 * end is unknown.
 * </p>
 */
export function isCustomsCrossing(
  originIso: string | null | undefined,
  destIso: string | null | undefined,
): boolean | null {
  const a = toCustomsTerritory(originIso);
  const b = toCustomsTerritory(destIso);
  if (a === null || b === null) return null;
  if (a === 'other' || b === 'other') {
    return originIso!.trim().toUpperCase() !== destIso!.trim().toUpperCase();
  }
  if (a === b) return false;
  if ((a === 'EU' && b === 'XI') || (a === 'XI' && b === 'EU')) return false;
  if (a === 'XI' && b === 'GB') return false;
  return true;
}
